/**
 * Accordion showcase — custom element (Dawn-style).
 * Mobile: expand/collapse rows with image in panel.
 * Desktop (≥720px CSS): left list + synced right media stack.
 */
class AccordionShowcase extends HTMLElement {
  constructor() {
    super();
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onIntersect = this._onIntersect.bind(this);
    /** @type {{ item: Element; btn: Element }[]} */
    this._bindings = [];
    /** @type {Element[]} */
    this._visuals = [];
    /** @type {HTMLElement[]} */
    this._progressBars = [];
    this._autoRotateTimer = null;
    this._autoRotateEnabled = false;
    this._autoRotatePaused = false;
    this._autoRotateInterval = 4000;
    this._autoRotateStartedAt = 0;
    this._autoRotateElapsed = 0;
    this._progressRaf = null;
    this._observer = null;
    this._isInViewport = false;
    this._wasInViewport = false;
  }

  connectedCallback() {
    this._teardown();
    const items = Array.from(this.querySelectorAll("[data-accordion-item]"));
    this._visuals = Array.from(this.querySelectorAll("[data-visual-index]"));
    this._autoRotateEnabled = this.dataset.autoplay === "true";
    this._autoRotateInterval = Math.max(
      2000,
      parseInt(this.dataset.autoplayInterval || "4000", 10) || 4000
    );

    items.forEach((item) => {
      const btn = item.querySelector(".accordion-showcase__trigger");
      if (!btn) return;
      this._ensureProgressBar(item);
      btn.addEventListener("click", this._onTriggerClick);
      this._bindings.push({ item, btn });
    });

    if (this._autoRotateEnabled && this._bindings.length > 1) {
      document.addEventListener("visibilitychange", this._onVisibilityChange);
      this._observer = new IntersectionObserver(this._onIntersect, {
        threshold: [0, 0.15, 0.35],
      });
      this._observer.observe(this);
    }
  }

  disconnectedCallback() {
    this._teardown();
  }

  _teardown() {
    this._stopAutoRotate();
    this._bindings.forEach(({ btn }) => {
      btn.removeEventListener("click", this._onTriggerClick);
    });
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    this._observer?.disconnect();
    this._observer = null;
    this._bindings = [];
    this._visuals = [];
    this._progressBars = [];
    this._isInViewport = false;
    this._wasInViewport = false;
  }

  /**
   * @param {Event} ev
   */
  _onTriggerClick(ev) {
    const btn = ev.currentTarget;
    if (!(btn instanceof Element)) return;
    const item = btn.closest("[data-accordion-item]");
    if (!item) return;
    const idx = parseInt(item.getAttribute("data-index") || "-1", 10);
    if (Number.isNaN(idx)) return;

    // If the clicked row is already open, advance to the next one
    // (wrapping back to the first after the last). Otherwise open it.
    const total = this._bindings.length;
    const isOpen = item.classList.contains("is-open");
    const nextIdx = isOpen && total > 0 ? (idx + 1) % total : idx;
    this.setOpen(nextIdx);
    this._restartAutoRotate();
  }

  _onVisibilityChange() {
    if (document.hidden) {
      this._pauseAutoRotate();
      return;
    }
    this._resumeAutoRotate();
  }

  _onIntersect(entries) {
    const entry = entries[0];
    if (!entry) return;
    this._isInViewport = entry.isIntersecting && entry.intersectionRatio > 0.15;
    if (this._isInViewport) {
      if (!this._wasInViewport) {
        this._restartAutoRotate();
      }
      this._wasInViewport = true;
      return;
    }
    this._wasInViewport = false;
    this._pauseAutoRotate();
  }

  _startAutoRotate() {
    if (
      !this._autoRotateEnabled ||
      this._autoRotatePaused ||
      !this._isInViewport ||
      this._bindings.length <= 1
    ) {
      return;
    }
    this._stopAutoRotate();
    this._autoRotateStartedAt = performance.now() - this._autoRotateElapsed;
    this._startProgressLoop();
    this._autoRotateTimer = window.setTimeout(() => {
      this._advanceAutoRotate();
    }, this._autoRotateInterval);
  }

  _stopAutoRotate() {
    if (this._autoRotateTimer) {
      window.clearTimeout(this._autoRotateTimer);
      this._autoRotateTimer = null;
    }
    this._stopProgressLoop();
  }

  _pauseAutoRotate() {
    if (!this._autoRotatePaused && this._autoRotateStartedAt > 0) {
      this._autoRotateElapsed = Math.min(
        this._autoRotateInterval,
        performance.now() - this._autoRotateStartedAt
      );
    }
    this._autoRotatePaused = true;
    this._stopAutoRotate();
  }

  _resumeAutoRotate() {
    if (!this._isInViewport) return;
    this._autoRotatePaused = false;
    this._startAutoRotate();
  }

  _restartAutoRotate() {
    if (!this._autoRotateEnabled) return;
    this._autoRotatePaused = false;
    this._autoRotateElapsed = 0;
    this._renderProgress(this._getOpenIndex(), 0);
    this._startAutoRotate();
  }

  _advanceAutoRotate() {
    const currentIndex = this._bindings.findIndex(({ item }) =>
      item.classList.contains("is-open")
    );
    const nextIndex =
      currentIndex >= 0
        ? (currentIndex + 1) % this._bindings.length
        : 0;
    this.setOpen(nextIndex);
    this._autoRotateElapsed = 0;
    this._startAutoRotate();
  }

  _ensureProgressBar(item) {
    if (!(item instanceof HTMLElement)) return;
    let progress = item.querySelector(".accordion-showcase__progress");
    if (!(progress instanceof HTMLElement)) {
      progress = document.createElement("div");
      progress.className = "accordion-showcase__progress";
      const fill = document.createElement("div");
      fill.className = "accordion-showcase__progress-fill";
      progress.appendChild(fill);
      item.appendChild(progress);
    }
    this._progressBars.push(progress);
  }

  _getOpenIndex() {
    return this._bindings.findIndex(({ item }) => item.classList.contains("is-open"));
  }

  _startProgressLoop() {
    this._stopProgressLoop();
    const tick = () => {
      if (!this._autoRotateEnabled || this._autoRotatePaused) return;
      const openIndex = this._getOpenIndex();
      const elapsed = Math.min(
        this._autoRotateInterval,
        performance.now() - this._autoRotateStartedAt
      );
      this._autoRotateElapsed = elapsed;
      this._renderProgress(
        openIndex,
        this._autoRotateInterval > 0 ? elapsed / this._autoRotateInterval : 0
      );
      this._progressRaf = window.requestAnimationFrame(tick);
    };
    this._progressRaf = window.requestAnimationFrame(tick);
  }

  _stopProgressLoop() {
    if (this._progressRaf) {
      window.cancelAnimationFrame(this._progressRaf);
      this._progressRaf = null;
    }
  }

  _renderProgress(activeIndex, ratio) {
    this._progressBars.forEach((progressBar, index) => {
      if (!(progressBar instanceof HTMLElement)) return;
      const fill = progressBar.firstElementChild;
      if (!(fill instanceof HTMLElement)) return;
      const value = index === activeIndex ? Math.max(0, Math.min(ratio, 1)) : 0;
      fill.style.setProperty("--acc-progress", value.toFixed(4));
    });
  }

  /**
   * @param {number} index
   */
  setOpen(index) {
    this._bindings.forEach(({ item, btn }) => {
      const i = parseInt(item.getAttribute("data-index") || "-1", 10);
      const open = i === index;
      item.classList.toggle("is-open", open);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
    });
    this._visuals.forEach((v) => {
      const vi = parseInt(v.getAttribute("data-visual-index") || "-1", 10);
      v.classList.toggle("is-active", vi === index);
    });
    this._renderProgress(index, 0);
  }
}

if (!customElements.get("accordion-showcase")) {
  customElements.define("accordion-showcase", AccordionShowcase);
}
